import { IdToken, useAuth0 } from "@auth0/auth0-react";
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Message, useChat as useAiChat } from "ai/react";
import dayjs from "dayjs";
import { v4 as uuid } from "uuid";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { useInterval } from "@/hooks/useInterval";
import { params } from "@/params";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { InteractionType } from "@/v2/entities/interaction-in-training/InteractionInTraining.model";
import { useErrorModal } from "@/components/errorModal";
import { useFetchTopics } from "@/routes/simulator/chat/api";
import { sleep } from "@/v2/assets/utils";
import { SendMessageDataParams } from "./type";
import { useSimulator } from "./SimulatorWrapper";
import { applyContextDefaults } from "./contextDefaults";
import {
  newReleasePolicyToInstructionJson,
  applyTemporarySuggestionToPolicy,
} from "./v1Helper";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { useSlackExternalApi } from "@/v2/entities/slack-external/SlackExternal.api";
import { useHitlSlackChannelId } from "@/v2/shared/hooks/useHitlSlackChannelId/useHitlSlackChannelId";
import { AsyncReplyHandler } from "@/v2/shared/hooks/useAsyncReplySubscription/AsyncReplyHandler";
import type {
  ReplyProgressUpdate,
  ReplyCompletedUpdate,
  ReplyErrorUpdate,
} from "@/v2/shared/hooks/useAsyncReplySubscription/useAsyncReplySubscription";

interface ChatWrapperContextType {
  conversationId: number | null;
  messages: Message[];
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => void;
  setInput: (input: string) => void;
  append: (message: Message) => void;
  reload: () => void;
  setMessages: (messages: Message[]) => void;
  isLoading: boolean;
  isProcessingMessage: boolean;
  isGeneratingTopic: boolean;
  releaseConversationId: number | null;
  reset: () => void;
  setProcessingMessage: (loading: boolean) => void;
  setGeneratingTopic: (loading: boolean) => void;
  ErrorModal: () => JSX.Element;
  // Async processing status
  asyncProgress: ReplyProgressUpdate | null;
  isAsyncProcessing: boolean;
  selectedTopicReason: string | null;
  messageSources: {
    interactionInTrainingId?: number;
    interactionType?: InteractionType;
    conversationId?: string;
    ticketId?: string;
  };
  setMessageSources: (messageSources: {
    interactionInTrainingId?: number;
    interactionType?: InteractionType;
    conversationId?: string;
    ticketId?: string;
  }) => void;
  isChatLoading: boolean;
  selectedTopic: Topic | null;
  handleTopicAssignment: (question: string, descriptionOverrides?: Record<number, string>) => Promise<string | undefined>;
  baseNewSession: (noTopic?: boolean) => void;
  messagesContextExport: {
    [key: string]: unknown;
  };
  handleTopicSelect: (topic: Topic | null) => void;
  handleAddMessage: (params: SendMessageDataParams) => Promise<void>;
  additionalContextFieldsValue: Record<string, any>;
  setAdditionalContextFieldsValue: (
    additionalContextFieldsValue: Record<string, any>
  ) => void;
  includeDrafts: boolean;
  setIncludeDrafts: (includeDrafts: boolean) => void;
}

export const ChatWrapperContext = createContext<
  ChatWrapperContextType | undefined
>(undefined);

interface ChatWrapperProps {
  skillsOverride?: Skill[];
}

export function ChatWrapper({
  children,
  skillsOverride,
}: PropsWithChildren<ChatWrapperProps>) {
  const {
    simulator,
    currentRelease,
    topics,
    upsertConversation,
    currentReleaseSkills,
    refreshReleaseData,
    temporaryPolicyOverride,
  } = useSimulator();
  const { workspace } = useWorkspace();
  const { sendSlackMessageExternal } = useSlackExternalApi();
  const { channelId: hitlSlackChannelId } = useHitlSlackChannelId();
  const { user } = useAuth0();
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();

  const queryParams = new URLSearchParams(window.location.search);

  const [selectedTopic, setSelectedTopicState] = useState<Topic | null>(null);
  const [selectedTopicReason, setSelectedTopicReason] = useState<string | null>(
    null
  );
  const [additionalContextFieldsValue, setAdditionalContextFieldsValue] =
    useState<Record<string, any>>({});
  const [includeDrafts, setIncludeDrafts] = useState(false);

  const setSelectedTopic = (topic: Topic | null, reason?: string) => {
    setSelectedTopicState(topic);
    if (reason) {
      setSelectedTopicReason(reason);
    } else {
      setSelectedTopicReason(null);
    }
  };

  const sessionIdRef = useRef<string>(String(dayjs().valueOf()));
  const dbConversationIdRef = useRef<number | null>(null);

  const [isProcessingMessage, setProcessingMessage] = useState(false);
  const [isGeneratingTopic, setGeneratingTopic] = useState(false);
  const [messagesContextExport, setMessagesContextExport] = useState<{
    [key: string]: unknown;
  }>({});
  const { openToggle, ErrorModal: ErrorModalComponent } = useErrorModal();

  const [messageSources, setMessageSources] = useState<{
    interactionInTrainingId?: number;
    interactionType?: InteractionType;
    conversationId?: string;
    ticketId?: string;
  }>({});

  // Async processing state
  const [asyncRequestId, setAsyncRequestId] = useState<string | null>(null);
  const [isAsyncProcessing, setIsAsyncProcessing] = useState(false);
  const [asyncProgress, setAsyncProgress] =
    useState<ReplyProgressUpdate | null>(null);
  const pendingAsyncMessageIdRef = useRef<string | null>(null);
  // Ref to store setMessages so async handlers can access it
  const setMessagesRef = useRef<
    ((messages: Message[] | ((prev: Message[]) => Message[])) => void) | null
  >(null);

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  useInterval(() => {
    (async () => {
      if (isAuthenticated) {
        const token = await getIdTokenClaims();
        setToken(token);
      }
    })();
  }, 1000 * 60 * 10); // every 10 minutes

  const authToken = tokenId?.__raw ? tokenId.__raw : "";
  const headers: Record<string, string> = {
    Authorization: `Bearer ${authToken}`,
  };

  if (workspace) {
    headers["x-tenant-id"] = workspace;
  }

  const skills = useMemo(() => {
    return skillsOverride ?? currentReleaseSkills ?? [];
  }, [skillsOverride, currentReleaseSkills]);

  const api = useMemo(() => {
    return `${params.API_URL}/platform/release-conversation/message`;
  }, []);

  const customFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      if (init?.body && typeof init.body === "string") {
        try {
          const body = JSON.parse(init.body);

          if (
            body.messages &&
            Array.isArray(body.messages) &&
            body.messages.length > 0
          ) {
            const modifiedMessages = body.messages.map(
              (message: Message, index: number) => {
                const isLastMessage = index === body.messages.length - 1;

                if (isLastMessage) {
                  return message;
                }

                const { annotations, ...messageWithoutAnnotations } = message;
                return messageWithoutAnnotations;
              }
            );

            body.messages = modifiedMessages;

            const modifiedInit = {
              ...init,
              body: JSON.stringify(body),
            };

            return fetch(input, modifiedInit);
          }
        } catch (error) {
          console.error("Error modifying request body:", error);
        }
      }

      return fetch(input, init);
    },
    []
  );

  const topicsBrief = useMemo(() => {
    return currentRelease?.topicReleasePolicies?.find(
      (t) => t.topicId === selectedTopic?.id
    )?.brief;
  }, [currentRelease, selectedTopic]);

  const oldStructureInstruction = useMemo(() => {
    if (!currentRelease || !topics) return [];
    const baseInstructions = newReleasePolicyToInstructionJson(
      currentRelease,
      topics,
      selectedTopic?.id ?? undefined
    );

    // Apply temporary policy override if exists
    if (temporaryPolicyOverride) {
      return applyTemporarySuggestionToPolicy(
        baseInstructions,
        temporaryPolicyOverride,
        topics
      );
    }

    return baseInstructions;
  }, [currentRelease, selectedTopic, topics, temporaryPolicyOverride]);

  useEffect(() => {
    const topicId = queryParams.get("topicId");
    if (topicId) {
      const topic = topics?.find((t: Topic) => t.id.toString() === topicId);
      if (topic) {
        setSelectedTopic(topic);
      } else {
        const otherTopic = topics?.find(
          (t: Topic) => t.topic.toLowerCase() === "other"
        );
        if (otherTopic) {
          setSelectedTopic(otherTopic);
        }
      }
    }
  }, [queryParams, topics]);

  const handleAsyncCompleted = useCallback(
    (response: ReplyCompletedUpdate) => {
      if (
        response.text &&
        pendingAsyncMessageIdRef.current &&
        setMessagesRef.current
      ) {
        const messageId = pendingAsyncMessageIdRef.current;
        const assistantMessage: Message = {
          id: messageId,
          role: "assistant",
          content: response.text,
          createdAt: new Date(),
          annotations: [
            {
              contextExport: response.contextExport,
              chainOfThoughtSteps: response.chainOfThoughtSteps,
              toolResults: response.toolResults,
            },
          ] as Message["annotations"],
        };

        // Update messages to replace the pending message or add new one
        setMessagesRef.current((prev) => {
          const existingIndex = prev.findIndex((m) => m.id === messageId);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = assistantMessage;
            return updated;
          }
          return [...prev, assistantMessage];
        });

        // Update context export (sources for the message)
        setMessagesContextExport((prev) => ({
          ...prev,
          [assistantMessage.id]: response.contextExport,
        }));
      }

      // Reset async state
      setAsyncRequestId(null);
      setIsAsyncProcessing(false);
      setAsyncProgress(null);
      pendingAsyncMessageIdRef.current = null;
      setProcessingMessage(false);
    },
    [setMessagesContextExport, setProcessingMessage]
  );

  // Handler for async errors
  const handleAsyncError = useCallback(
    (error: ReplyErrorUpdate) => {
      console.error("Async reply error", error);
      setAsyncRequestId(null);
      setIsAsyncProcessing(false);
      setAsyncProgress(null);
      pendingAsyncMessageIdRef.current = null;
      setProcessingMessage(false);
      openToggle();
    },
    [openToggle, setProcessingMessage]
  );

  // Handler for async progress updates
  const handleAsyncProgress = useCallback((progress: ReplyProgressUpdate) => {
    setAsyncProgress(progress);
  }, []);

  // Handler for subscribing state changes
  const handleSubscribingChange = useCallback((isSubscribing: boolean) => {
    setIsAsyncProcessing(isSubscribing);
  }, []);

  const handleResponse = useCallback(
    async (response: Message) => {
      if (!currentRelease) return;

      // Check for async-pending annotation (browser skill processing)
      const asyncPendingAnnotation = response.annotations?.find(
        (ann: unknown) => (ann as { type?: string })?.type === "async-pending"
      ) as
        | { type: string; requestId: string; subscription?: unknown }
        | undefined;

      if (asyncPendingAnnotation?.requestId) {
        // Store the message ID so we can update it when async completes
        pendingAsyncMessageIdRef.current = response.id;
        // Start subscription - this will keep isProcessingMessage true
        setAsyncRequestId(asyncPendingAnnotation.requestId);
        // Don't set processing to false - async is still running
        return;
      }

      setMessagesContextExport((prev) => {
        const contextExport = (response?.annotations?.[0] as any)
          ?.contextExport;
        return {
          ...prev,
          [response.id]: contextExport,
        };
      });

      setProcessingMessage(false);

      try {
        await refreshReleaseData();
      } catch (error) {
        console.error("Error refreshing release data:", error);
      }
    },
    [
      currentRelease,
      upsertConversation,
      selectedTopic?.id,
      simulator?.id,
      setMessagesContextExport,
      setProcessingMessage,
      refreshReleaseData,
      setAsyncRequestId,
    ]
  );

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    setInput,
    append,
    reload,
    setMessages,
    isLoading,
  } = useAiChat({
    id: sessionIdRef.current,
    api,
    headers,
    fetch: customFetch,
    sendExtraMessageFields: true,
    generateId: () => uuid(),
    body: {
      id: sessionIdRef.current,
      channel: simulator?.channel,
      tenant: workspace || user?.owner,
      tenantId: workspace || user?.owner,
      instructions: oldStructureInstruction,
      topicId: selectedTopic?.id,
      topic: selectedTopic?.topic,
      subTopic: "",
      brief: topicsBrief,
      skills,
      simulatorId: simulator?.id,
      releaseId: currentRelease?.id,
      seperateGoodFeedback: simulator?.seperateGoodFeedback,
      customToneOfVoice: currentRelease?.customToneOfVoice,
      interactionInTrainingId: messageSources?.interactionInTrainingId,
      interactionType: messageSources?.interactionType,
      conversationId: messageSources?.conversationId,
      ticketId: messageSources?.ticketId,
      additionalContext: applyContextDefaults(
        additionalContextFieldsValue,
        workspace,
      ),
      allowDrafts: includeDrafts,
      slackChannelId: hitlSlackChannelId,
    },
    onResponse: (response: any) => {
      const conversationId =
        response.headers.get("x-release-conversation-id") ?? null;
      if (conversationId) {
        const backendConversationId = Number(conversationId);
        dbConversationIdRef.current = backendConversationId;
      }
    },
    onFinish: handleResponse,
    onError: (error: Error) => {
      console.error("Chat error:", error);
      try {
        // window.quack?.proactiveTrigger();
      } catch (e) {
        console.error("Error in onError callback:", e);
      }
      openToggle();
      try {
        sendSlackMessageExternal({
          channelName: "webapp-critical",
          message: `Error in Training Panel\n\nTenant: ${
            workspace || user?.owner
          }\nSimulator: ${simulator?.id}\nRelease: ${
            currentRelease?.id
          }\nConversation ID: ${dbConversationIdRef.current}\n
          Inquiries: ${messages
            .map((m: Message) =>
              m.role === "user" ? "User:" + m.content : "Assistant:" + m.content
            )
            .join("\n")}`,
        });
      } catch (e) {
        console.error("Failed to send Slack notification:", e);
      }
    },
  });

  // Update ref so async handlers can access setMessages
  useEffect(() => {
    setMessagesRef.current = setMessages;
  }, [setMessages]);

  const handleSubmitWithDelay = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  const reset = useCallback(() => {
    sessionIdRef.current = String(dayjs().valueOf());
    dbConversationIdRef.current = null;
  }, []);

  const { mutateAsync: fetchTopics } = useFetchTopics();

  const handleTopicAssignment = useCallback(
    async (
      question: string,
      descriptionOverrides?: Record<number, string>
    ) => {
      if (!topics || !currentRelease) {
        console.warn(
          "Topics or currentRelease not available for topic assignment"
        );
        return;
      }

      setGeneratingTopic(true);
      try {
        const data: { text: string; reason: string } = await fetchTopics(
          {
            question: question,
            topics: topics
              ?.map((topic: { topic: string }) => topic.topic)
              .filter((topic: string) => topic.toLowerCase() !== "other"),
            topicsBriefs: topics?.reduce(
              (
                acc: Record<string, string>,
                topic: { topic: string; description?: string; id: number }
              ) => {
                if (
                  descriptionOverrides &&
                  topic.id in descriptionOverrides
                ) {
                  acc[topic.topic] = descriptionOverrides[topic.id];
                } else {
                  acc[topic.topic] = topic.description ?? "";
                }
                return acc;
              },
              {}
            ),
            tenant: workspace || user?.owner,
          },
          {
            onError: (error) => {
              console.error("Error fetching topics:", error);
              // window.quack?.proactiveTrigger();
            },
          }
        );

        const topic = topics?.filter(
          (topic: { topic: string; id: number }) =>
            topic.topic.replace(/['"`*]/g, "").trim() ===
            data.text.replace(/['"`*]/g, "").trim()
        )?.[0];

        if (!topic) {
          const otherTopic = topics?.find(
            (t: Topic) => t.topic.toLowerCase() === "other"
          );
          if (otherTopic) {
            setSelectedTopic(otherTopic, data.reason);
          }
        } else {
          setSelectedTopic(topic, data.reason);
        }

        await new Promise((resolve) => setTimeout(resolve, 500));

        return data.text.replace(/['"`*]/g, "").trim();
      } catch (error) {
        console.error("Error in handleTopicAssignment:", error);
        throw error;
      } finally {
        setGeneratingTopic(false);
      }
    },
    [fetchTopics, topics, currentRelease, setSelectedTopic, simulator?.id]
  );

  const ErrorModal = useCallback(
    () => (
      <ErrorModalComponent
        title="Seems like something went wrong"
        description="We automatically sent a full report of this issue to our team, and we will update you as soon as possible. In the meantime please try to clear your cache and logout and login again."
        actionText="Close"
        onAction={() => {
          openToggle();
        }}
      />
    ),
    [openToggle]
  );

  const baseNewSession = useCallback(
    (noTopic?: boolean) => {
      reset();
      if (noTopic) {
        setSelectedTopic(null);
      }
      setMessages([]);
      setMessageSources({});
    },
    [reset]
  );

  const handleTopicSelect = useCallback(
    (topic: Topic | null) => {
      baseNewSession(true);
      setSelectedTopic(topic);
    },
    [baseNewSession, setSelectedTopic]
  );

  const handleAddMessage = useCallback(
    async ({
      messages,
      ticketId,
      conversationId,
      pendingReview,
      descriptionOverrides,
    }: SendMessageDataParams) => {
      baseNewSession();

      if (pendingReview || conversationId || ticketId) {
        setMessageSources({
          interactionInTrainingId: pendingReview?.id,
          interactionType: pendingReview?.interactionType,
          conversationId: conversationId,
          ticketId: ticketId,
        });
      }

      try {
        await sleep(200);

        if (messages.length > 1) {
          const allButLast = messages.slice(0, -1).map((msg) => ({
            id: uuid(),
            content: msg.content,
            role: msg.role,
          }));
          setMessages(allButLast);
        }

        const transcript = messages
          .map((msg) => `${msg.role}: ${msg.content}`)
          .join("\n");
        setGeneratingTopic(true);
        try {
          await handleTopicAssignment(transcript, descriptionOverrides);
          await sleep(500);
        } finally {
          setGeneratingTopic(false);
          setProcessingMessage(true);
        }

        const lastMessage = {
          id: uuid(),
          content: messages[messages.length - 1].content,
          role: messages[messages.length - 1].role,
        };
        append(lastMessage);
      } catch (error) {
        openToggle();
      } finally {
        setProcessingMessage(false);
      }
    },
    [
      handleTopicAssignment,
      append,
      setMessages,
      setProcessingMessage,
      setGeneratingTopic,
      baseNewSession,
      setMessageSources,
    ]
  );

  const value: ChatWrapperContextType = {
    conversationId: dbConversationIdRef.current,
    selectedTopic,
    isChatLoading: isLoading || isGeneratingTopic || isAsyncProcessing,
    messages,
    input,
    handleInputChange,
    handleSubmit: handleSubmitWithDelay,
    append,
    reload,
    setMessages,
    isLoading,
    setInput,
    releaseConversationId: dbConversationIdRef.current,
    reset,
    isProcessingMessage: isProcessingMessage || isAsyncProcessing,
    isGeneratingTopic,
    setProcessingMessage,
    setGeneratingTopic,
    setMessageSources,
    messageSources,
    ErrorModal,
    handleTopicAssignment,
    baseNewSession,
    messagesContextExport,
    handleTopicSelect,
    handleAddMessage,
    selectedTopicReason,
    additionalContextFieldsValue,
    setAdditionalContextFieldsValue,
    // Async processing status
    asyncProgress,
    isAsyncProcessing,
    includeDrafts,
    setIncludeDrafts,
  };

  return (
    <ChatWrapperContext.Provider value={value}>
      {asyncRequestId && (
        <AsyncReplyHandler
          key={asyncRequestId}
          requestId={asyncRequestId}
          onCompleted={handleAsyncCompleted}
          onError={handleAsyncError}
          onProgress={handleAsyncProgress}
          onSubscribingChange={handleSubscribingChange}
        />
      )}
      {children}
    </ChatWrapperContext.Provider>
  );
}

export function useChat(): ChatWrapperContextType {
  const context = useContext(ChatWrapperContext);
  if (context === undefined) {
    throw new Error("useSimulator must be used within a SimulatorWrapper");
  }
  return context;
}

export function useChatOptional(): ChatWrapperContextType | null {
  return useContext(ChatWrapperContext) ?? null;
}
